import { useEffect, useState } from "react";
import { getApiUrl } from "../api";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

export default function ForgotPassword({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const [step, setStep] = useState("request");
  const [login, setLogin] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = isArabic ? "إعادة تعيين كلمة المرور | مؤسسة تراحم الخيرية" : "Reset Password | Tarahum Charity Foundation";
  }, [isArabic]);

  const callApi = async (body) => {
    const response = await fetch(getApiUrl("/api/ForgotPassword.ashx"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.message || "Unable to reset the password.");
    return result;
  };

  const showError = (error) => {
    setIsError(true);
    setMessage(error.message || (isArabic ? "تعذر إعادة تعيين كلمة المرور." : "Unable to reset the password."));
  };

  const requestCode = async (event) => {
    event.preventDefault();
    setSaving(true); setMessage(""); setIsError(false);
    try {
      const result = await callApi({ action: "request", login: login.trim() });
      setChallengeId(result.challengeId);
      setMaskedMobile(result.maskedMobile || "");
      setStep("verify");
      setMessage(result.message || "");
    } catch (error) { showError(error); } finally { setSaving(false); }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setSaving(true); setMessage(""); setIsError(false);
    try {
      const result = await callApi({ action: "verify", challengeId, otp });
      setStep("reset");
      setMessage(result.message || "");
    } catch (error) { showError(error); } finally { setSaving(false); }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setMessage(""); setIsError(false);
    if (password.length < 8) { showError(new Error(isArabic ? "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل." : "Password must contain at least 8 characters.")); return; }
    if (password !== confirmPassword) { showError(new Error(isArabic ? "كلمتا المرور غير متطابقتين." : "Password and confirmation do not match.")); return; }
    setSaving(true);
    try {
      const result = await callApi({ action: "reset", challengeId, password });
      setStep("complete");
      setMessage(result.message || "");
    } catch (error) { showError(error); } finally { setSaving(false); }
  };

  const title = step === "complete"
    ? (isArabic ? "تم تغيير كلمة المرور" : "Password Changed")
    : (isArabic ? "إعادة تعيين كلمة المرور" : "Reset Your Password");

  return <>
    <Preloader />
    {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
    <section className={`login-mdn${isArabic ? " arabic-login-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
      <div className="container"><div className="login-mdn-card">
        <div className="login-mdn-left"><div className="login-mdn-brand"><div className="login-mdn-secure">{isArabic ? "استعادة آمنة" : "Secure Recovery"}</div></div></div>
        <div className="login-mdn-right"><div className="login-mdn-form">
          <div className="login-mdn-title-badge"><span></span>{isArabic ? "حماية الحساب" : "Account Protection"}</div>
          <h2>{title}</h2>
          <p className="desc">{isArabic ? "تحقق من رقم الهاتف المرتبط بحسابك، ثم أنشئ كلمة مرور جديدة." : "Verify the mobile number linked to your account, then create a new password."}</p>
          {message ? <div className={`login-mdn-message${isError ? " error" : ""}`} role="alert">{message}</div> : null}

          {step === "request" ? <form onSubmit={requestCode}>
            <div className="login-mdn-group"><label htmlFor="reset-login">{isArabic ? "البريد الإلكتروني أو رقم الهاتف" : "Email Address or Mobile Number"}</label><div className="login-mdn-input">
              <input id="reset-login" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" placeholder={isArabic ? "أدخل البريد الإلكتروني أو رقم الهاتف" : "Enter email address or mobile number"} required />
              <span className="icon" aria-hidden="true">@</span>
            </div></div>
            <button className="login-mdn-submit" type="submit" disabled={saving}>{saving ? (isArabic ? "جارٍ الإرسال..." : "Sending...") : (isArabic ? "إرسال رمز التحقق" : "Send Verification Code")}</button>
          </form> : null}

          {step === "verify" ? <form onSubmit={verifyCode}>
            <div className="login-otp-mobile"><span>{isArabic ? "تم إرسال الرمز إلى" : "Code sent to"}</span><strong dir="ltr">{maskedMobile}</strong></div>
            <div className="login-mdn-group"><label htmlFor="reset-otp">{isArabic ? "رمز التحقق المكون من 6 أرقام" : "6-Digit Verification Code"}</label><div className="login-mdn-input login-otp-input">
              <input id="reset-otp" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength="6" placeholder="000000" required autoFocus />
              <span className="icon" aria-hidden="true"><i className="fa-solid fa-shield-halved"></i></span>
            </div></div>
            <button className="login-mdn-submit" type="submit" disabled={saving || otp.length !== 6}>{saving ? (isArabic ? "جارٍ التحقق..." : "Verifying...") : (isArabic ? "تحقق من الرمز" : "Verify Code")}</button>
          </form> : null}

          {step === "reset" ? <form onSubmit={resetPassword}>
            <div className="login-mdn-group"><label htmlFor="new-password">{isArabic ? "كلمة المرور الجديدة" : "New Password"}</label><div className="login-mdn-input"><input id="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength="8" autoComplete="new-password" required /></div></div>
            <div className="login-mdn-group"><label htmlFor="confirm-password">{isArabic ? "تأكيد كلمة المرور" : "Confirm New Password"}</label><div className="login-mdn-input"><input id="confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength="8" autoComplete="new-password" required /></div></div>
            <button className="login-mdn-submit" type="submit" disabled={saving}>{saving ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isArabic ? "حفظ كلمة المرور" : "Save New Password")}</button>
          </form> : null}

          <p className="login-mdn-signup"><Link to={isArabic ? "/ar/login" : "/login"}>{step === "complete" ? (isArabic ? "العودة لتسجيل الدخول" : "Return to Login") : (isArabic ? "العودة" : "Back to Login")}</Link></p>
        </div></div>
      </div></div>
    </section>
    {isArabic ? <ArabicFooter /> : <Footer />}
    <FloatingWidgets {...interactions} /><HomeModals {...interactions} />
  </>;
}
